import {
  forwardRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
} from 'react';
import {
  AlertCircle,
  Eye,
  EyeOff,
  Lock,
  type LucideIcon,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface AuthFieldProps extends Omit<ComponentProps<typeof Input>, 'id'> {
  id: string;
  label: string;
  icon: LucideIcon;
  error?: string;
  hint?: string;
}

export const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(
  function AuthField(
    { id, label, icon: Icon, error, hint, className, ...inputProps },
    ref,
  ) {
    const descriptionId = error
      ? `${id}-error`
      : hint
        ? `${id}-hint`
        : undefined;

    return (
      <div>
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium">
          {label}
        </label>
        <div className="relative">
          <Icon
            aria-hidden="true"
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            {...inputProps}
            ref={ref}
            id={id}
            aria-describedby={descriptionId}
            aria-invalid={Boolean(error)}
            className={cn(
              'h-12 rounded-xl bg-background pl-10',
              error &&
                'border-destructive focus-visible:ring-destructive/20',
              className,
            )}
          />
        </div>
        {error ? (
          <p
            id={`${id}-error`}
            className="mt-1.5 text-xs font-medium text-destructive"
          >
            {error}
          </p>
        ) : hint ? (
          <p id={`${id}-hint`} className="mt-1.5 text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);

interface PasswordFieldProps
  extends Omit<AuthFieldProps, 'icon' | 'type'> {
  showPasswordLabel: string;
  hidePasswordLabel: string;
  capsLockMessage: string;
}

export const PasswordField = forwardRef<
  HTMLInputElement,
  PasswordFieldProps
>(function PasswordField(
  {
    id,
    label,
    error,
    hint,
    showPasswordLabel,
    hidePasswordLabel,
    capsLockMessage,
    className,
    onKeyDown,
    onKeyUp,
    ...inputProps
  },
  ref,
) {
  const [isVisible, setIsVisible] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);
  const descriptionId = error
    ? `${id}-error`
    : isCapsLockOn
      ? `${id}-caps-lock`
      : hint
        ? `${id}-hint`
        : undefined;

  const updateCapsLock = (event: KeyboardEvent<HTMLInputElement>) => {
    setIsCapsLockOn(event.getModifierState('CapsLock'));
  };

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium">
        {label}
      </label>
      <div className="relative">
        <Lock
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          {...inputProps}
          ref={ref}
          id={id}
          type={isVisible ? 'text' : 'password'}
          aria-describedby={descriptionId}
          aria-invalid={Boolean(error)}
          className={cn(
            'h-12 rounded-xl bg-background pl-10 pr-12',
            error && 'border-destructive focus-visible:ring-destructive/20',
            className,
          )}
          onKeyDown={(event) => {
            updateCapsLock(event);
            onKeyDown?.(event);
          }}
          onKeyUp={(event) => {
            updateCapsLock(event);
            onKeyUp?.(event);
          }}
        />
        <button
          type="button"
          className="absolute right-0 top-0 flex h-12 w-12 items-center justify-center rounded-r-xl text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          aria-label={isVisible ? hidePasswordLabel : showPasswordLabel}
          aria-pressed={isVisible}
          onClick={() => setIsVisible((current) => !current)}
        >
          {isVisible ? (
            <EyeOff aria-hidden="true" className="h-4 w-4" />
          ) : (
            <Eye aria-hidden="true" className="h-4 w-4" />
          )}
        </button>
      </div>
      {error ? (
        <p
          id={`${id}-error`}
          className="mt-1.5 text-xs font-medium text-destructive"
        >
          {error}
        </p>
      ) : isCapsLockOn ? (
        <p
          id={`${id}-caps-lock`}
          className="mt-1.5 text-xs font-medium text-amber-600"
        >
          {capsLockMessage}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

export function AuthAlert({ message }: { message: string }) {
  if (!message) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex gap-2.5 rounded-xl border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive"
    >
      <AlertCircle
        aria-hidden="true"
        className="mt-0.5 h-4 w-4 shrink-0"
      />
      <span>{message}</span>
    </div>
  );
}
